import OpencareAnalytics from '@opencare/analytics';
import React, { useCallback, useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';

import { useHistory } from 'react-router-dom';

import CONFIG from 'app.config';
import { selectDataClinic, selectDataMatch } from 'connect/selectors';

import { RESULTS_PATH } from 'containers/App/Routes';

import { useOfferService } from 'services/OfferService';
import { useRedirectionService } from 'services/RedirectionService';
import Logger from 'utils/Logger';

import { Button, CloseButton, CtaContainer, NoOffersModalContainer, NoOffersModalContent, OfferNameText, Subtitle, TextContentContainer, Title } from './styled-components';

const analytics = new OpencareAnalytics();

interface NoOffersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
}

const getCspUrl = (canonicalPath: string) => {
  const parts = canonicalPath?.split('/').filter(Boolean);
  if (!parts?.length) {
    return null;
  }
  const searchV1Url = CONFIG.SEARCH_APP_URL;
  return `${searchV1Url}/${parts.slice(0, 2).join('/')}/`;
};

export const NoOffersModal = ({ isOpen, onClose, onContinue }: NoOffersModalProps) => {
  const history = useHistory();
  const redirectionService = useRedirectionService();
  const offerService = useOfferService();

  const match = useSelector(selectDataMatch);
  const clinic = useSelector(selectDataClinic);

  const hasMatch = useMemo(() => Boolean(match?.id), [match]);
  const cspUrl = useMemo(
    () => getCspUrl(clinic.canonicalPath),
    [clinic.canonicalPath],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    analytics.trackPageView({
      pageType: analytics.pageTypes.PRACTICE_PROFILE,
      pageName: 'clinic-profile',
      name: 'profile-no-offers-modal',
    });
  }, [isOpen]);

  const onContinueButtonClick = useCallback(() => {
    analytics.trackClick({
      pageType: analytics.pageTypes.PRACTICE_PROFILE,
      pageName: 'clinic-profile',
      name: 'profile-no-offers-modal',
      section: 'continue-with-booking',
    });

    onContinue();
  }, [onContinue]);

  const onFindProvidersButtonClick = useCallback(() => {
    analytics.trackClick({
      pageType: analytics.pageTypes.PRACTICE_PROFILE,
      pageName: 'clinic-profile',
      name: 'profile-no-offers-modal',
      section: 'find-providers-with-rewards',
      matchId: match?.id,
      practiceId: clinic.id,
    });

    if (hasMatch) {
      history.push(RESULTS_PATH);
      return;
    }

    if (!cspUrl) {
      Logger.error('No CSP URL found for clinic', { clinicId: clinic.id });
      return;
    }

    redirectionService.redirect({
      url: cspUrl,
      reason: 'no-offers-modal-find-providers',
    });
  }, [hasMatch, history, match?.id, cspUrl, redirectionService, clinic.id]);

  const onCloseButtonClick = useCallback(() => {
    analytics.trackClick({
      pageType: analytics.pageTypes.PRACTICE_PROFILE,
      pageName: 'clinic-profile',
      name: 'profile-no-offers-modal',
      section: 'close',
    });

    onClose();
  }, [onClose]);

  return (
    <NoOffersModalContainer visible={isOpen} afterClose={onClose}>
      <CloseButton onClick={onCloseButtonClick} />
      <NoOffersModalContent>
        <TextContentContainer>
          <Title>This provider isn’t part of the reward program</Title>
          <Subtitle>
            You can still book your appointment with them,
            but you will not qualify for the
            {' '}<OfferNameText>{offerService.offerName}</OfferNameText>.
          </Subtitle>
        </TextContentContainer>
        <CtaContainer>
          <Button $type="continue" onClick={onContinueButtonClick}>
            Continue with booking
          </Button>
          <Button $type="find-providers" onClick={onFindProvidersButtonClick}>
            Find providers with rewards
          </Button>
          <Button $type="go-back" onClick={onCloseButtonClick}>
            Go back
          </Button>
        </CtaContainer>
      </NoOffersModalContent>
    </NoOffersModalContainer>
  );
};
